import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Table,
  Badge,
  Spinner,
  Alert,
  Progress,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Input,
  InputGroup,
  InputGroupText
} from "reactstrap"
import Chart from "react-apexcharts"
import {
  getRFQProjectAnalytics,
  getRFQProjectAnalyticsDetails,
  exportRFQProjectAnalyticsDetails,
  formatCurrency,
  formatPercentage,
  getChartColors,
  truncateText
} from "../../../../helpers/rfq_dashboard_api"
import InfoButton from "./InfoButton"

const ProjectAnalytics = ({ filters, onRefresh }) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState('')
  const [modalTitle, setModalTitle] = useState('')
  const [modalProjectId, setModalProjectId] = useState(null)
  const [modalData, setModalData] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalPage, setModalPage] = useState(1)
  const [modalSearch, setModalSearch] = useState('')
  const [modalPerPage, setModalPerPage] = useState(10)
  const [exportLoading, setExportLoading] = useState(false)

  // Handle card/chart click to open modal
  const handleCardClick = (type, title, projectId = null, additionalParams = {}) => {
    setModalType(type)
    setModalTitle(title)
    setModalProjectId(projectId)
    setModalPage(1)
    setModalSearch('')
    setModalPerPage(10) // Reset to default
    setModalOpen(true)
    loadModalData(type, projectId, 1, '', additionalParams, 10)
  }

  // Load modal data
  const loadModalData = async (type, projectId, page = 1, search = '', additionalParams = {}, perPage = modalPerPage) => {
    try {
      setModalLoading(true)

      const params = {
        type: type,
        project_id: projectId,
        page: page,
        per_page: perPage,
        search: search,
        ...additionalParams
      }

      // Apply global filters but preserve the project_id from chart click
      const filtersToApply = { ...filters }
      if (projectId) {
        // Don't override project_id if it was passed from chart click
        delete filtersToApply.project_id
      }
      Object.assign(params, filtersToApply)

      console.log('loadModalData params:', { type, projectId, params })

      const response = await getRFQProjectAnalyticsDetails(params)

      if (response && response.status) {
        setModalData(response.data)
      } else {
        throw new Error(response?.message || "Failed to load modal data")
      }
    } catch (err) {
      console.error("Modal data loading error:", err)
      setModalData(null)
    } finally {
      setModalLoading(false)
    }
  }

  // Handle modal pagination
  const handleModalPageChange = (page) => {
    setModalPage(page)
    loadModalData(modalType, modalProjectId, page, modalSearch, {}, modalPerPage)
  }

  // Handle modal search with debouncing
  const handleModalSearch = (searchValue) => {
    setModalSearch(searchValue)
    setModalPage(1)

    // Debounce search to avoid too many API calls
    clearTimeout(window.projectAnalyticsSearchTimeout)
    window.projectAnalyticsSearchTimeout = setTimeout(() => {
      loadModalData(modalType, modalProjectId, 1, searchValue, {}, modalPerPage)
    }, 500)
  }

  // Handle modal export
  const handleModalExport = async () => {
    try {
      setExportLoading(true)

      const params = {
        type: modalType,
        project_id: modalProjectId,
        search: modalSearch
      }

      // Apply global filters but preserve the project_id from modal
      const filtersToApply = { ...filters }
      if (modalProjectId) {
        // Don't override project_id if it was set from modal
        delete filtersToApply.project_id
      }
      Object.assign(params, filtersToApply)

      console.log('handleModalExport params:', { modalType, modalProjectId, params })

      await exportRFQProjectAnalyticsDetails(params)
    } catch (err) {
      console.error("Export error:", err)
      alert("Export failed. Please try again.")
    } finally {
      setExportLoading(false)
    }
  }

  // Load project analytics data
  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Mock data for demonstration
      const mockData = {
        project_analytics: [
          {
            project_id: 1,
            project_name: 'Corporate Tower',
            project_type: 'Commercial',
            location: 'Mumbai',
            total_rfqs: 15,
            assigned_rfqs: 12,
            assignment_rate: 80.0,
            total_budget: 1200000,
            total_final_amount: 1150000,
            total_paid: 460000,
            payment_completion_rate: 40.0,
            avg_rfq_age_days: 25
          },
          {
            project_id: 2,
            project_name: 'Mall Complex',
            project_type: 'Retail',
            location: 'Delhi',
            total_rfqs: 20,
            assigned_rfqs: 18,
            assignment_rate: 90.0,
            total_budget: 800000,
            total_final_amount: 750000,
            total_paid: 300000,
            payment_completion_rate: 40.0,
            avg_rfq_age_days: 18
          },
          {
            project_id: 3,
            project_name: 'Residential Block A',
            project_type: 'Residential',
            location: 'Bangalore',
            total_rfqs: 10,
            assigned_rfqs: 8,
            assignment_rate: 80.0,
            total_budget: 500000,
            total_final_amount: 450000,
            total_paid: 180000,
            payment_completion_rate: 40.0,
            avg_rfq_age_days: 30
          }
        ]
      }

      // Use real API call
      try {
        const response = await getRFQProjectAnalytics(filters)
        console.log("Project analytics response:", response)

        if (response && response.status) {
          setData(response.data)
          console.log("Project analytics loaded successfully:", response.data)
        } else {
          throw new Error(response?.message || "Failed to load project analytics")
        }
      } catch (apiError) {
        console.warn("Failed to load project analytics, using mock data:", apiError)
        setData(mockData)
      }
    } catch (err) {
      console.error("Project analytics loading error:", err)
      setError(err.message || "Failed to load project analytics")
    } finally {
      setLoading(false)
    }
  }

  // Load data when filters change
  useEffect(() => {
    loadData()
  }, [filters])

  // Loading state
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <div className="mt-3">
          <h5>Loading Project Analytics...</h5>
          <p className="text-muted">Please wait while we fetch the data</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <Alert color="danger" className="text-center">
        <h4 className="alert-heading">Error Loading Project Analytics</h4>
        <p>{error}</p>
        <button className="btn btn-primary" onClick={loadData}>
          <i className="bx bx-refresh me-1"></i>
          Try Again
        </button>
      </Alert>
    )
  }

  if (!data || !data.project_analytics) {
    return (
      <div className="text-center py-5">
        <i className="bx bx-building display-4 text-muted"></i>
        <h5 className="mt-3">No Project Analytics Data Available</h5>
        <p className="text-muted">Please adjust your filters and try again.</p>
      </div>
    )
  }

  const { project_analytics, summary } = data

  // Debug logging for budget vs Final Amount analysis
  console.log('Project Analytics Data:', project_analytics)
  console.log('Summary Data:', summary)

  // Use summary data from backend if available, otherwise calculate from project_analytics
  const totals = summary || project_analytics.reduce((acc, project) => ({
    total_projects: project_analytics.length,
    total_rfqs: acc.total_rfqs + (parseInt(project.total_rfqs) || 0),
    assigned_rfqs: acc.assigned_rfqs + (parseInt(project.assigned_rfqs) || 0),
    total_budget: acc.total_budget + (parseFloat(project.total_budget) || 0),
    final_budget: acc.final_budget + (parseFloat(project.final_budget) || 0),
    total_remaining: acc.total_remaining + (parseFloat(project.total_remaining) || 0)
  }), {
    total_projects: project_analytics.length,
    total_rfqs: 0,
    assigned_rfqs: 0,
    total_budget: 0,
    final_budget: 0,
    total_remaining: 0
  })

  // Prepare RFQ distribution chart
  const rfqDistributionOptions = {
    chart: {
      type: 'bar',
      height: 400,
      fontFamily: 'inherit',
      toolbar: {
        show: false
      },
      offsetY: -10,
      events: {
        dataPointSelection: function(event, chartContext, config) {
          const projectIndex = config.dataPointIndex
          const project = project_analytics[projectIndex]
          if (project) {
            handleCardClick('rfq_distribution', `${project.project_name} - RFQ Details`, project.project_id)
          }
        }
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded'
      },
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: project_analytics.map(project => project.project_name || 'Unknown'),
      title: {
        text: 'Projects',
        style: {
          fontSize: '14px',
          fontFamily: 'inherit',
          fontWeight: 600,
          color: '#495057'
        }
      },
      labels: {
        rotate: -15,
        style: {
          fontSize: '11px',
          fontFamily: 'inherit',
          fontWeight: 500,
          colors: '#74788d'
        },
        maxHeight: 80,
        trim: false,
        hideOverlappingLabels: false,
        offsetY: 5
      },
      axisBorder: {
        show: true,
        color: '#e9ecef',
        height: 1,
        width: '100%',
        offsetX: 0,
        offsetY: 0
      },
      axisTicks: {
        show: true,
        borderType: 'solid',
        color: '#e9ecef',
        height: 6,
        offsetX: 0,
        offsetY: 0
      }
    },
    yaxis: {
      title: {
        text: 'Number of RFQs',
        style: {
          fontSize: '14px',
          fontFamily: 'inherit',
          fontWeight: 600,
          color: '#495057'
        }
      },
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: 'inherit',
          colors: '#74788d'
        }
      },
      min: 0,
      forceNiceScale: true
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: function({series, seriesIndex, dataPointIndex, w}) {
        const project = project_analytics[dataPointIndex]
        if (!project) return ''

        const totalRFQs = parseInt(project.total_rfqs) || 0
        const assignedRFQs = parseInt(project.assigned_rfqs) || 0
        const closedRFQs = parseInt(project.closed_rfqs) || 0

        return `
          <div class="custom-tooltip p-3">
            <div class="tooltip-title mb-2"><strong>${project.project_name}</strong></div>
            <div class="tooltip-content">
              <div class="d-flex justify-content-between mb-1">
                <span>Total RFQs:</span>
                <strong class="text-primary">${totalRFQs}</strong>
              </div>
              <div class="d-flex justify-content-between mb-1">
                <span>Assigned RFQs:</span>
                <strong class="text-success">${assignedRFQs}</strong>
              </div>
              <div class="d-flex justify-content-between">
                <span>Closed RFQs:</span>
                <strong class="text-warning">${closedRFQs}</strong>
              </div>
            </div>
          </div>
        `
      }
    },
    colors: getChartColors(),
    legend: {
      position: 'top',
      fontSize: '14px',
      fontFamily: 'inherit',
      fontWeight: 500,
      markers: {
        width: 12,
        height: 12,
        radius: 6
      }
    },
    grid: {
      borderColor: '#f1f1f1',
      strokeDashArray: 3
    },
    responsive: [{
      breakpoint: 768,
      options: {
        chart: {
          height: 450
        },
        xaxis: {
          labels: {
            rotate: -45,
            fontSize: '10px',
            maxHeight: 100,
            offsetY: 10
          }
        }
      }
    }, {
      breakpoint: 480,
      options: {
        chart: {
          height: 400
        },
        xaxis: {
          labels: {
            rotate: -45,
            fontSize: '9px',
            maxHeight: 120,
            offsetY: 15
          }
        },
        legend: {
          position: 'bottom',
          fontSize: '12px'
        }
      }
    }]
  }

  const rfqDistributionSeries = [
    {
      name: 'Total RFQs',
      data: project_analytics.map(project => parseInt(project.total_rfqs) || 0)
    },
    {
      name: 'Assigned RFQs',
      data: project_analytics.map(project => parseInt(project.assigned_rfqs) || 0)
    },
    {
      name: 'Closed RFQs',
      data: project_analytics.map(project => parseInt(project.closed_rfqs) || 0)
    }
  ]

  // Prepare budget vs actual chart
  const budgetChartOptions = {
    chart: {
      type: 'bar',
      height: 350,
      events: {
        dataPointSelection: function(event, chartContext, config) {
          const projectIndex = config.dataPointIndex
          const project = project_analytics[projectIndex]
          if (project) {
            handleCardClick('budget_analysis', `${project.project_name} - Budget Analysis`, project.project_id)
          }
        }
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        endingShape: 'rounded'
      },
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    xaxis: {
      categories: project_analytics.map(project => truncateText(project.project_name, 15)),
      title: {
        text: 'Projects'
      }
    },
    yaxis: {
      title: {
        text: 'Amount (₹)'
      },
      labels: {
        formatter: function (val) {
          return formatCurrency(val)
        }
      }
    },
    fill: {
      opacity: 1
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return formatCurrency(val)
        }
      }
    },
    colors: getChartColors()
  }

  const budgetChartSeries = [
    {
      name: 'Budget',
      data: project_analytics.map(project => {
        const budget = parseFloat(project.total_budget) || 0
        console.log(`Project: ${project.project_name}, Budget: ${budget}`)
        return budget
      })
    },
    {
      name: 'Final Amount',
      data: project_analytics.map(project => {
        const actual = parseFloat(project.total_final_amount) || 0
        console.log(`Project: ${project.project_name}, Final: ${actual}`)
        return actual
      })
    }
  ]

  // Get project type distribution
  const projectTypes = project_analytics.reduce((acc, project) => {
    const type = project.project_type || 'Unknown'
    acc[type] = (acc[type] || 0) + 1
    return acc
  }, {})

  const projectTypeChartOptions = {
    chart: {
      type: 'donut',
      height: 300,
      events: {
        dataPointSelection: function(event, chartContext, config) {
          const typeIndex = config.dataPointIndex
          const projectType = Object.keys(projectTypes)[typeIndex]
          if (projectType) {
            handleCardClick('project_types', `${projectType} Projects`, null, { project_type: projectType })
          }
        }
      }
    },
    labels: Object.keys(projectTypes),
    colors: getChartColors(),
    legend: {
      position: 'bottom'
    },
    plotOptions: {
      pie: {
        donut: {
          size: '70%'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%"
      }
    }
  }

  const projectTypeChartSeries = Object.values(projectTypes)

  return (
    <div className="project-analytics fade-in">
      <style>{`
        .cursor-pointer {
          cursor: pointer;
        }
        .hover-highlight:hover {
          background-color: #f8f9fa;
          border-color: #007bff;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,123,255,0.15);
        }
        .hover-highlight:hover .text-primary {
          color: #0056b3 !important;
        }
        .hover-highlight:hover .text-success {
          color: #155724 !important;
        }
        .hover-highlight:hover .text-info {
          color: #0c5460 !important;
        }
        .hover-highlight:hover .text-warning {
          color: #856404 !important;
        }
        .metric-card {
          transition: all 0.3s ease;
        }
      `}</style>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card
            className="metric-card cursor-pointer hover-highlight"
            onClick={() => handleCardClick('total_projects', 'All Projects Overview')}
            title="Click to view all projects"
            style={{ position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="project_analytics_total_projects"
                title="Total Projects - Business Logic & Calculation"
                size="sm"
                
              />
            </div>
            <div className="text-center">
              <div className="metric-value">{totals.total_projects}</div>
              <div className="metric-label">Total Projects</div>
              <small className="text-primary">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </div>
          </Card>
        </Col>
        <Col md={3}>
          <Card
            className="metric-card success cursor-pointer hover-highlight"
            onClick={() => handleCardClick('total_rfqs', 'All RFQs Overview')}
            title="Click to view all RFQs"
            style={{ position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="project_analytics_total_rfqs"
                title="Total RFQs - Business Logic & Calculation"
                size="sm"
                
              />
            </div>
            <div className="text-center">
              <div className="metric-value">{totals.total_rfqs}</div>
              <div className="metric-label">Total RFQs</div>
              <small className="text-success">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </div>
          </Card>
        </Col>
        <Col md={3}>
          <Card
            className="metric-card info cursor-pointer hover-highlight"
            onClick={() => handleCardClick('total_budget', 'Budget Overview')}
            title="Click to view budget details"
            style={{ position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="project_analytics_total_budget"
                title="Total Budget - Business Logic & Calculation"
                size="sm"
                
              />
            </div>
            <div className="text-center">
              <div className="metric-value">{formatCurrency(totals.total_budget)}</div>
              <div className="metric-label">Total Budget</div>
              <small className="text-info">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </div>
          </Card>
        </Col>
        <Col md={3}>
          <Card
            className="metric-card warning cursor-pointer hover-highlight"
            onClick={() => handleCardClick('final_budget', 'Final Amount Overview')}
            title="Click to view final amount details"
            style={{ position: 'relative' }}
          >
            <div className="position-absolute" style={{ top: '8px', right: '8px', zIndex: 10 }}>
              <InfoButton
                type="project_analytics_final_budget"
                title="Final Amount - Business Logic & Calculation"
                size="sm"
                
              />
            </div>
            <div className="text-center">
              <div className="metric-value">{formatCurrency(totals.final_budget)}</div>
              <div className="metric-label">Final Amount</div>
              <small className="text-warning">
                <i className="bx bx-mouse me-1"></i>Click to view details
              </small>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="mb-4">
        <Col lg={8}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bx bx-bar-chart-alt me-2"></i>
                  RFQ Status Distribution by Project
                </span>
                <InfoButton
                  type="project_analytics"
                  title="RFQ Distribution Chart - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              <div className="rfq-distribution-chart-container">
                <Chart
                  options={rfqDistributionOptions}
                  series={rfqDistributionSeries}
                  type="bar"
                  height={400}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bx bx-pie-chart-alt me-2"></i>
                  Project Types
                </span>
                <InfoButton
                  type="project_analytics"
                  title="Project Types Chart - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              <Chart
                options={projectTypeChartOptions}
                series={projectTypeChartSeries}
                type="donut"
                height={395}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Budget Analysis Chart */}
      <Row className="mb-4">
        <Col lg={12}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bx bx-trending-up me-2"></i>
                  Budget vs Final Amount Analysis (Per Project)
                </span>
                <InfoButton
                  type="project_analytics"
                  title="Budget vs Final Amount Analysis - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              <Chart
                options={budgetChartOptions}
                series={budgetChartSeries}
                type="bar"
                height={350}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Detailed Project Table */}
      {/*<Row>
        <Col lg={12}>
          <Card className="dashboard-card">
            <CardBody>
              <CardTitle className="mb-4 d-flex justify-content-between align-items-center">
                <span>
                  <i className="bx bx-table me-2"></i>
                  Project Performance Details
                </span>
                <InfoButton
                  type="project_analytics"
                  title="Project Performance Table - Calculation Logic"
                  size="sm"
                />
              </CardTitle>
              <div className="table-responsive">
                <Table className="table-nowrap mb-0">
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Location</th>
                      <th>Type</th>
                      <th>RFQs</th>
                      <th>Assignment Rate</th>
                      <th>Budget</th>
                      <th>Final Amount</th>
                      <th>Payment Status</th>
                      <th>Avg Age</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project_analytics.map((project, index) => (
                      <tr key={index}>
                        <td>
                          <div>
                            <h6 className="mb-0">{project.project_name}</h6>
                            <small className="text-muted">ID: {project.project_id}</small>
                          </div>
                        </td>
                        <td>
                          <span className="text-muted">{project.location || 'N/A'}</span>
                        </td>
                        <td>
                          <Badge color="info" className="font-size-12">
                            {project.project_type || 'Unknown'}
                          </Badge>
                        </td>
                        <td>
                          <div>
                            <strong>{project.total_rfqs}</strong>
                            <small className="text-muted d-block">
                              {project.assigned_rfqs} assigned
                            </small>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                              <Progress
                                value={project.assignment_rate}
                                color={project.assignment_rate > 80 ? "success" : project.assignment_rate > 50 ? "warning" : "danger"}
                                className="progress-sm"
                              />
                            </div>
                            <div className="flex-shrink-0 ms-2">
                              <small>{formatPercentage(project.assignment_rate)}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <strong>{formatCurrency(project.total_budget)}</strong>
                        </td>
                        <td>
                          <strong className="text-success">
                            {formatCurrency(project.total_final_amount)}
                          </strong>
                        </td>
                        <td>
                          <div>
                            <div className="d-flex align-items-center mb-1">
                              <div className="flex-grow-1">
                                <Progress
                                  value={project.payment_completion_rate}
                                  color={project.payment_completion_rate > 80 ? "success" : project.payment_completion_rate > 50 ? "warning" : "danger"}
                                  className="progress-sm"
                                />
                              </div>
                              <div className="flex-shrink-0 ms-2">
                                <small>{formatPercentage(project.payment_completion_rate)}</small>
                              </div>
                            </div>
                            <small className="text-muted">
                              {formatCurrency(project.final_budget)} final budget
                            </small>
                          </div>
                        </td>
                        <td>
                          <span className="text-muted">
                            {project.avg_rfq_age_days ? `${Math.round(project.avg_rfq_age_days)} days` : 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>*/}

      {/* Details Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setModalOpen(false)}>
          <i className="bx bx-detail me-2"></i>
          {modalTitle}
        </ModalHeader>
        <ModalBody>
          {/* Search and Controls - Always visible like Overview tab */}
          <Row className="mb-3">
            <Col md={4}>
              <InputGroup>
                <InputGroupText>
                  <i className="bx bx-search"></i>
                </InputGroupText>
                <Input
                  type="text"
                  placeholder="Search RFQs, projects, vendors..."
                  value={modalSearch}
                  onChange={(e) => handleModalSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={2}>
              <div>
                <label className="form-label">Records per page</label>
                <Input
                  type="select"
                  value={modalPerPage}
                  onChange={(e) => {
                    const newPerPage = parseInt(e.target.value)
                    setModalPerPage(newPerPage)
                    setModalPage(1)
                    loadModalData(modalType, modalProjectId, 1, modalSearch, {}, newPerPage)
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>
              </div>
            </Col>
            <Col md={4}></Col>
            <Col md={2} className="text-end">
              <Button
                color="success"
                onClick={handleModalExport}
                disabled={exportLoading}
                block
              >
                {exportLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className="bx bx-download me-2"></i>
                    Export CSV
                  </>
                )}
              </Button>
            </Col>
          </Row>

          {/* Data section with loading state */}
          {modalLoading ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
              <p className="mt-2">Loading details...</p>
            </div>
          ) : modalData && modalData.records ? (
            <>
              {/* Data Table */}
              <div className="table-responsive">
                <Table className="table-nowrap mb-0">
                  <thead>
                    <tr>
                      {modalType === 'total_projects' ? (
                        <>
                          <th>Project Name</th>
                          <th>Property Type</th>
                          <th>RFQs</th>
                          <th>Budget</th>
                          <th>Final Amount</th>
                          <th>Actions</th>
                        </>
                      ) : modalType === 'total_budget' ? (
                        <>
                          <th>RFQ#</th>
                          <th>RFQ Title</th>
                          <th>Project</th>
                          <th>Vendor</th>
                          <th>Status</th>
                          <th>Budget</th>
                          <th>Final Amount</th>
                          <th>Estimated Date</th>
                          <th>Completion Date</th>
                          <th>Created Date</th>
                        </>
                      ) : modalType === 'final_budget' ? (
                        <>
                          <th>RFQ#</th>
                          <th>RFQ Title</th>
                          <th>Project</th>
                          <th>Vendor</th>
                          <th>Budget</th>
                          <th>Final Amount</th>
                          <th>Status</th>
                          <th>Estimated Date</th>
                          <th>Completion Date</th>
                          <th>Created Date</th>
                        </>
                      ) : (
                        <>
                          <th>RFQ#</th>
                          <th>RFQ Title</th>
                          <th>Project</th>
                          <th>Vendor</th>
                          <th>Status</th>
                          <th>Budget</th>
                          <th>Final Amount</th>
                          <th>Estimated Date</th>
                          <th>Completion Date</th>
                          <th>Created Date</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {modalData.records.length > 0 ? (
                      modalData.records.map((record, index) => (
                        <tr key={index}>
                          {modalType === 'total_projects' ? (
                            // Project List View
                            <>

                              <td>
                                <div>
                                  <strong>{record.project_name}</strong>
                                  {record.contract_terms_start_date && record.contract_terms_end_date && (
                                    <div>
                                      <small className="text-muted">
                                        {new Date(record.contract_terms_start_date).toLocaleDateString()} - {new Date(record.contract_terms_end_date).toLocaleDateString()}
                                      </small>
                                    </div>
                                  )}
                                  {record.project_area_sqft && (
                                    <div>
                                      <small className="text-info">
                                        {record.project_area_sqft} sqft, {record.total_floors} floors
                                      </small>
                                    </div>
                                  )}
                                </div>
                              </td>


                              <td>
                                <div>
                                  <Badge color="secondary" className="badge-soft mb-1">
                                    {record.property_type || 'N/A'}
                                  </Badge>
                                  <div>
                                    <small className="text-muted">
                                      {record.project_use || 'N/A'}
                                    </small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <strong className="text-primary">{record.total_rfqs}</strong>
                                  <div>
                                    <small className="text-success">{record.completed_rfqs} completed</small>
                                  </div>
                                  <div>
                                    <small className="text-info">{record.assigned_rfqs} assigned</small>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <strong>{formatCurrency(record.total_budget)}</strong>
                              </td>
                              <td>
                                <strong className="text-success">
                                  {formatCurrency(record.total_final_amount)}
                                </strong>
                              </td>
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleCardClick('rfq_distribution', `${record.project_name} - RFQ Details`, record.project_id)}
                                  title="View RFQs for this project"
                                >
                                  <i className="bx bx-list-ul me-1"></i>
                                  Open RFQ
                                </Button>
                              </td>
                            </>
                          ) : modalType === 'total_budget' ? (
                            // Budget Analysis View
                            <>
                              <td>
                                <strong>{record.rfq_code}</strong>
                              </td>
                              <td>{truncateText(record.rfq_title, 30)}</td>
                              <td>
                                <div>
                                  <strong>{record.project_name}</strong>
                                  <div>
                                    <small className="text-muted">ID: {record.project_id}</small>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <small>{record.vendor_name || 'Unassigned'}</small>
                              </td>
                              <td>
                                <Badge color="primary" className="badge-soft">
                                  {record.status}
                                </Badge>
                              </td>
                              <td>
                                <strong>{formatCurrency(record.budget)}</strong>
                              </td>
                              <td>
                                <strong className="text-success">
                                  {formatCurrency(record.total_final_amount)}
                                </strong>
                              </td>
                              <td>
                                {record.estimated_completion_date ? (
                                  <small className="text-info">
                                    <i className="bx bx-calendar me-1"></i>
                                    {record.estimated_completion_date}
                                  </small>
                                ) : (
                                  <small className="text-muted">Not Set</small>
                                )}
                              </td>
                              <td>
                                {record.completion_date ? (
                                  <small className="text-success">
                                    <i className="bx bx-check-circle me-1"></i>
                                    {record.completion_date}
                                  </small>
                                ) : (
                                  <small className="text-muted">-</small>
                                )}
                              </td>
                              <td>
                                <small className="text-muted">
                                  {new Date(record.created_at).toLocaleDateString()}
                                </small>
                              </td>
                            </>
                          ) : modalType === 'final_budget' ? (
                            // Final Budget Details View
                            <>
                              <td>
                                <strong>{record.rfq_code}</strong>
                              </td>
                              <td>{truncateText(record.rfq_title, 30)}</td>
                              <td>
                                <div>
                                  <strong>{record.project_name}</strong>
                                  <div>
                                    <small className="text-muted">ID: {record.project_id}</small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <small>{record.vendor_name || 'Unassigned'}</small>
                              </td>
                              <td>
                                <strong>{formatCurrency(record.budget)}</strong>
                              </td>
                              <td>
                                <strong>{formatCurrency(record.total_final_amount)}</strong>
                              </td>

                              <td>
                                <Badge color="primary" className="badge-soft">
                                  {record.status}
                                </Badge>
                              </td>
                              <td>
                                {record.estimated_completion_date ? (
                                  <small className="text-info">
                                    <i className="bx bx-calendar me-1"></i>
                                    {record.estimated_completion_date}
                                  </small>
                                ) : (
                                  <small className="text-muted">Not Set</small>
                                )}
                              </td>
                              <td>
                                {record.completion_date ? (
                                  <small className="text-success">
                                    <i className="bx bx-check-circle me-1"></i>
                                    {record.completion_date}
                                  </small>
                                ) : (
                                  <small className="text-muted">-</small>
                                )}
                              </td>
                              <td>
                                <small className="text-muted">
                                  {new Date(record.created_at).toLocaleDateString()}
                                </small>
                              </td>
                            </>
                          ) : (
                            // Standard RFQ List View
                            <>
                              <td>
                                <strong>{record.rfq_code}</strong>
                              </td>
                              <td>{truncateText(record.rfq_title, 30)}</td>
                              <td>
                                <div>
                                  <strong>{record.project_name}</strong>
                                  <div>
                                    <small className="text-muted">ID: {record.project_id}</small>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <small>{record.vendor_name || 'Unassigned'}</small>
                              </td>
                              <td>
                                <Badge color="primary" className="badge-soft">
                                  {record.status}
                                </Badge>
                              </td>
                              <td>
                                <strong>{formatCurrency(record.budget)}</strong>
                              </td>
                              <td>
                                <strong className="text-success">
                                  {formatCurrency(record.total_final_amount)}
                                </strong>
                              </td>
                              <td>
                                {record.estimated_completion_date ? (
                                  <small className="text-info">
                                    <i className="bx bx-calendar me-1"></i>
                                    {record.estimated_completion_date}
                                  </small>
                                ) : (
                                  <small className="text-muted">Not Set</small>
                                )}
                              </td>
                              <td>
                                {record.completion_date ? (
                                  <small className="text-success">
                                    <i className="bx bx-check-circle me-1"></i>
                                    {record.completion_date}
                                  </small>
                                ) : (
                                  <small className="text-muted">-</small>
                                )}
                              </td>
                              <td>
                                <small className="text-muted">
                                  {new Date(record.created_at).toLocaleDateString()}
                                </small>
                              </td>
                            </>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={
                          modalType === 'total_projects' ? "6" :
                          modalType === 'total_budget' ? "10" :
                          modalType === 'final_budget' ? "10" :
                          "11"
                        } className="text-center py-4">
                          <i className="bx bx-search display-4 text-muted"></i>
                          <p className="text-muted mt-2">No records found</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {modalData.records && modalData.records.length > 0 && (
                <Row className="mt-3">
                  <Col md={6}>
                    <p className="text-muted">
                      Showing {((modalData.pagination?.current_page || 1) - 1) * (modalData.pagination?.per_page || 10) + 1} to {((modalData.pagination?.current_page || 1) - 1) * (modalData.pagination?.per_page || 10) + modalData.records.length} of {modalData.pagination?.total_records || 0} records
                    </p>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end">
                      <Button
                        color="light"
                        size="sm"
                        disabled={!modalData.pagination?.has_prev_page}
                        onClick={() => handleModalPageChange(modalPage - 1)}
                        className="me-2"
                      >
                        <i className="bx bx-chevron-left"></i>
                      </Button>
                      <span className="align-self-center me-2">
                        Page {modalData.pagination?.current_page || 1} of {modalData.pagination?.total_pages || 1}
                      </span>
                      <Button
                        color="light"
                        size="sm"
                        disabled={!modalData.pagination?.has_next_page}
                        onClick={() => handleModalPageChange(modalPage + 1)}
                      >
                        <i className="bx bx-chevron-right"></i>
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}
            </>
          ) : (
            <div className="text-center py-4">
              <i className="bx bx-error display-4 text-muted"></i>
              <p className="text-muted mt-2">Failed to load data</p>
            </div>
          )}
        </ModalBody>
      </Modal>
    </div>
  )
}

export default ProjectAnalytics
